import datapoints from './datapoints';
import { deleteConversationsWithoutConfirmDialog } from './deleteConversations';
import { deleteItemsWithoutConfirmDialog } from './deleteItems';
import blockLastSendersHelper from './helpers/blockLastSendersHelper';
import { getLastSenderMailboxesFromRowKeys } from './helpers/getMailboxes';
import getUniqueConversationContextsForRowKey from './helpers/getUniqueConversationContextsForRowKey';
import { getRowIdsFromRowKeys, TableView } from 'owa-mail-list-store';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { ActionSource } from 'owa-mail-store';
import { doesTableSupportUndo } from 'owa-mail-undo';
import DisposalType from 'owa-service/lib/contract/DisposalType';
import ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import { trace } from 'owa-trace';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';

/**
 * Block the smtp addresses from given rows
 * @param rowKeys the row keys
 * @param tableView the table view
 * @param actionSource the action source that triggers the block action
 */
export default function blockLastSendersFromTable(
    rowKeys: string[],
    tableView: TableView,
    actionSource: ActionSource
) {
    if (!rowKeys || rowKeys.length == 0) {
        trace.error(
            'Block button should not be exposed when there is nothing selected in list view.'
        );
        return;
    }

    // Get de-duped mailboxes
    const mailboxes = getLastSenderMailboxesFromRowKeys(rowKeys, tableView);
    blockLastSendersHelper(mailboxes, () => {
        deleteRowsUponBlocking(rowKeys, tableView, actionSource);
    });
}

/**
 * Delete the rows after the block last senders
 * @param rowKeys of the rows where the last senders are retrieved from
 * @param tableView that contains the rows
 */
const deleteRowsUponBlocking = createLegacyActionWithDatapoint(
    'deleteRowsUponBlocking',
    datapoints.blockSendersFromTable
)(function deleteRowsUponBlocking(
    rowKeys: string[],
    tableView: TableView,
    actionSource: ActionSource
) {
    // Since conversations may have been deleted from the table while the confirm dialog was open,
    // we need to validate the conversation rowKeys again before performing delete
    const filteredRowKeys = rowKeys.filter(rowKey => tableView.rowKeys.indexOf(rowKey) >= 0);
    const disposableType = isConsumer() ? 'HardDelete' : 'MoveToDeletedItems';
    const instrumentationContexts = getInstrumentationContextsFromTableView(rowKeys, tableView);
    switch (tableView.tableQuery.listViewType) {
        case ReactListViewType.Conversation:
            const conversationActionContexts = getUniqueConversationContextsForRowKey(
                filteredRowKeys,
                tableView.id
            );

            // VSO: 20069: [Discussion] Block Senders on a conversation
            // Permanently delete selected emails
            deleteConversationsWithoutConfirmDialog(
                conversationActionContexts,
                tableView.id,
                disposableType as DisposalType,
                instrumentationContexts
            );
            break;

        case ReactListViewType.Message:
            const itemIdStrings = getRowIdsFromRowKeys(rowKeys, tableView.id);
            deleteItemsWithoutConfirmDialog(
                itemIdStrings,
                disposableType as DisposalType,
                tableView.tableQuery.folderId,
                null /* actionSource */,
                instrumentationContexts,
                null /* requestInitOptions */,
                doesTableSupportUndo(tableView.tableQuery.folderId, getMailboxInfo(tableView)) &&
                    disposableType === 'MoveToDeletedItems'
            );
            break;
    }
});
